import { motion, AnimatePresence, useMotionValue, useTransform, animate } from "framer-motion";
import { useState, useEffect, useMemo } from "react";

/* --------------------------------------------------------------------------
   Firm Operations Snapshot
   Replaces the old ROI calculator on the homepage and pricing page.
   Interactive slider + firm-type selector → live operational load estimates.
   Lead capture posts to /api/leads and opens a printable /report page.
   -------------------------------------------------------------------------- */

const FIRM_TYPES = [
    { id: "solo",       label: "The Solo Practice",      subtitle: "1–2 attorneys",                              intakes: 8,   gen: 1 },
    { id: "family",     label: "The Family Firm",         subtitle: "3–9 attorneys · your name on the door",      intakes: 30,  gen: 2 },
    { id: "growth",     label: "The Growth Firm",         subtitle: "10–24 attorneys",                            intakes: 85,  gen: 3 },
    { id: "regional",   label: "The Regional Powerhouse", subtitle: "25–74 attorneys",                            intakes: 220, gen: 4 },
    { id: "institution",label: "The Institution",         subtitle: "75+ attorneys",                              intakes: 600, gen: 5 },
];

/* One line of empathy per firm shape — the pulse Lexafide has felt sitting inside these firms. */
const FIRM_INSIGHTS = {
    solo:        "Every call is you. Every follow-up is you. The next signed case and the next unpaid hour both live in the same inbox — yours.",
    family:      "Your name is on the wall. You know every client by voice. And every Thursday, the docket still asks you to be in three places at once.",
    growth:      "You've hired paralegals, added tools, and calls still fall through. The system isn't broken — the system doesn't exist yet.",
    regional:    "You've replaced heroes with process. Now the process itself needs its own operations layer just to keep the wheels turning.",
    institution: "At your scale, every 1% of leaked efficiency is a full team. You're not shopping for tools — you're designing a machine.",
};

const AUTOMATION_RATE = 0.60;
const HOURLY_COST = 38;

/* Firms we can serve today. Solo practices see the numbers but a warmer, honest CTA. */
const SERVED_FIRM_IDS = new Set(["family", "growth", "regional", "institution"]);

const round0 = (n) => Math.round(n);

const computeMetrics = (intakes) => {
    const newInquiries  = intakes * 3;
    const retainersSent = intakes * 1.4;
    const activeMatters = intakes * 14;
    const inboundCalls  = newInquiries * 2 + activeMatters * 0.8;

    // Round each hour component so what's displayed adds up cleanly.
    const receptionHours = Math.round((inboundCalls * 11) / 60);
    const intakeHours    = Math.round((newInquiries * 25) / 60);
    const followupHours  = Math.round((activeMatters * 20) / 60);
    const voicemailHours = Math.round((inboundCalls * 0.15 * 8) / 60);

    const opsHours       = receptionHours + intakeHours + followupHours + voicemailHours;
    const reclaimedHours = Math.round(opsHours * AUTOMATION_RATE);
    const dollarValue    = reclaimedHours * HOURLY_COST * 12;
    const fullTimeStaff  = opsHours / 160;

    return {
        newInquiries, retainersSent, activeMatters, inboundCalls,
        receptionHours, intakeHours, followupHours, voicemailHours,
        opsHours, reclaimedHours, dollarValue, fullTimeStaff,
    };
};

const AnimatedNum = ({ value, prefix = "", suffix = "", decimals = 0 }) => {
    const mv = useMotionValue(0);
    const display = useTransform(mv, (v) =>
        `${prefix}${Number(v).toLocaleString("en-US", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        })}${suffix}`
    );
    useEffect(() => {
        const c = animate(mv, value, { duration: 0.55, ease: [0.22, 1, 0.36, 1] });
        return c.stop;
    }, [value, mv]);
    return <motion.span>{display}</motion.span>;
};

const FirmOperationsSnapshot = () => {
    const [firmId, setFirmId] = useState("family");
    const firm = useMemo(() => FIRM_TYPES.find((f) => f.id === firmId), [firmId]);
    const [intakes, setIntakes] = useState(firm.intakes);
    const [modalOpen, setModalOpen] = useState(false);

    // Changing firm type resets the slider to its default.
    useEffect(() => {
        setIntakes(firm.intakes);
    }, [firm]);

    const m = useMemo(() => computeMetrics(intakes), [intakes]);

    return (
        <section
            id="roi"
            data-testid="firm-snapshot-section"
            className="relative py-20 md:py-28 bg-[var(--cream-2)] border-y border-[var(--border)]"
        >
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6 }}
                    className="mb-10 md:mb-14 max-w-3xl"
                >
                    <span className="eyebrow-pill">Firm operations snapshot</span>
                    <h2 className="font-display text-[clamp(2rem,4.2vw,3.2rem)] leading-[1.05] font-semibold text-[var(--ink)] mt-5">
                        See what&apos;s really on your team&apos;s{" "}
                        <span className="accent-blue">plate.</span>
                    </h2>
                    <p className="mt-5 text-lg text-[var(--ink-soft)]">
                        Pick the firm that looks like yours. We&apos;ll show you the operational load hiding behind your monthly intakes — and how much of it never needs to touch your staff.
                    </p>
                </motion.div>

                {/* Step 1 — firm type selector */}
                <div className="mb-6 md:mb-8">
                    <label className="font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--ink-dim)] block mb-3">
                        Step 1 · Pick your firm shape
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2.5">
                        {FIRM_TYPES.map((f) => {
                            const active = f.id === firmId;
                            return (
                                <button
                                    key={f.id}
                                    type="button"
                                    onClick={() => setFirmId(f.id)}
                                    aria-pressed={active}
                                    data-testid={`firm-type-${f.id}`}
                                    className={`relative text-left rounded-xl border px-4 py-4 transition-all duration-300 overflow-hidden ${
                                        active
                                            ? "border-[var(--blue)] bg-[var(--paper)] shadow-[0_20px_50px_-22px_rgba(30,91,255,0.35)]"
                                            : "border-[var(--border-strong)] bg-[var(--paper)] text-[var(--ink)] hover:border-[var(--ink)] hover:-translate-y-0.5"
                                    }`}
                                    style={active ? { boxShadow: "0 20px 50px -22px rgba(30,91,255,0.35), inset 0 0 0 1px rgba(30,91,255,0.35)" } : undefined}
                                >
                                    {active && (
                                        <span
                                            aria-hidden
                                            className="pointer-events-none absolute inset-0"
                                            style={{
                                                background: "linear-gradient(135deg, rgba(30,91,255,0.09) 0%, rgba(30,91,255,0) 60%)",
                                            }}
                                        />
                                    )}
                                    <div className="relative flex items-start justify-between gap-2 mb-1.5">
                                        <div className="font-mono text-[10px] tracking-[0.18em] uppercase"
                                             style={{ color: active ? "var(--blue)" : "var(--muted)" }}>
                                            Gen {f.gen}
                                        </div>
                                        {active && (
                                            <span className="relative flex h-1.5 w-1.5 mt-1" aria-hidden>
                                                <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--blue)] opacity-75 animate-ping" />
                                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[var(--blue)]" />
                                            </span>
                                        )}
                                    </div>
                                    <div className="relative font-display text-[15px] leading-tight font-semibold text-[var(--ink)]">
                                        {f.label}
                                    </div>
                                    <div className="relative text-[12px] mt-1 leading-snug text-[var(--ink-soft)]">
                                        {f.subtitle}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Per-firm empathy insight — fades in on selection */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={firmId}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                            data-testid="firm-insight-quote"
                            className="mt-5 flex items-start gap-4 rounded-xl border-l-2 border-[var(--blue)] bg-[var(--paper)] px-5 py-4 md:px-6 md:py-5 shadow-[0_16px_36px_-24px_rgba(30,91,255,0.35)]"
                        >
                            <i className="fa-solid fa-quote-left accent-blue text-lg opacity-60 flex-shrink-0 mt-1" />
                            <div>
                                <p className="font-display font-medium text-[15.5px] md:text-[17px] leading-[1.35] text-[var(--ink)]">
                                    {FIRM_INSIGHTS[firmId]}
                                </p>
                                <div className="mt-2 font-mono text-[9.5px] tracking-[0.2em] uppercase text-[var(--muted)]">
                                    What {firm.label.toLowerCase()}s tell us
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Step 2 — slider + Step 3 — results */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-5 md:gap-6">
                    {/* Left — slider + assumptions */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.6 }}
                        className="card-paper p-6 md:p-8 flex flex-col"
                    >
                        <div className="flex items-end justify-between mb-3">
                            <label
                                htmlFor="fs-intakes"
                                className="font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--ink-dim)]"
                            >
                                Step 2 · Signed cases per month
                            </label>
                            <span
                                data-testid="fs-intakes-value"
                                className="font-display text-3xl text-[var(--ink)] tabular-nums font-semibold leading-none"
                            >
                                {intakes.toLocaleString()}
                            </span>
                        </div>
                        <input
                            id="fs-intakes"
                            data-testid="fs-intakes-slider"
                            type="range"
                            min="2"
                            max="1500"
                            step="1"
                            value={intakes}
                            onChange={(e) => setIntakes(Number(e.target.value))}
                            className="fs-slider w-full"
                            aria-label="Signed cases per month"
                        />
                        <div className="flex justify-between mt-2 font-mono text-[10px] text-[var(--ink-dim)] tracking-[0.15em]">
                            <span>2</span>
                            <span>1,500</span>
                        </div>

                        {/* Derived numbers preview */}
                        <div className="mt-7 grid grid-cols-2 gap-3">
                            <MiniStat testid="fs-new-inquiries" label="New inquiries / mo" value={round0(m.newInquiries)} />
                            <MiniStat testid="fs-retainers" label="Retainers sent / mo" value={round0(m.retainersSent)} />
                            <MiniStat testid="fs-active-matters" label="Active matters" value={round0(m.activeMatters)} />
                            <MiniStat testid="fs-inbound-calls" label="Inbound calls / mo" value={round0(m.inboundCalls)} />
                        </div>

                        <div className="mt-5 rounded-lg bg-[var(--cream-2)] border border-[var(--border)] px-3.5 py-2.5 flex items-center justify-between">
                            <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-[var(--ink-dim)]">
                                Avg. matter lifecycle
                            </span>
                            <span className="font-display text-sm font-semibold text-[var(--ink)]">14 months</span>
                        </div>

                        {/* Assumptions — always visible */}
                        <div className="mt-auto pt-6 border-t border-[var(--border)]">
                            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--ink-dim)]">
                                Assumptions
                            </div>
                            <p className="mt-3 text-[12.5px] text-[var(--ink-soft)] leading-relaxed" data-testid="fs-assumptions-body">
                                Benchmarks from plaintiff firms in production on Lexafide: ~33% inquiry-to-signed conversion, 14-month average matter lifecycle, 11 min average call handle time, 60% automation rate, ${HOURLY_COST}/hr blended paralegal cost. Estimates — your firm will vary.
                            </p>
                        </div>
                    </motion.div>

                    {/* Right — hero result panel */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.6, delay: 0.05 }}
                        className="rounded-2xl bg-[var(--ink)] text-white p-6 md:p-8 flex flex-col overflow-hidden"
                        data-testid="fs-results-panel"
                    >
                        <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/55">
                            Step 3 · What&apos;s on your team&apos;s plate
                        </span>

                        <div className="mt-5">
                            <p className="text-white/60 text-sm">Operational hours / month</p>
                            <div
                                data-testid="fs-ops-hours"
                                className="font-display text-[clamp(3rem,6.2vw,5rem)] leading-none font-semibold tracking-tight tabular-nums mt-2"
                            >
                                <AnimatedNum value={m.opsHours} suffix=" hrs" />
                            </div>
                            <p className="mt-3 text-[15px] text-white/70">
                                ≈{" "}
                                <span className="text-white tabular-nums font-semibold">
                                    <AnimatedNum value={m.fullTimeStaff} decimals={1} />
                                </span>{" "}
                                full-time staff doing operational work
                            </p>
                        </div>

                        {/* Highlighted reclaim bar */}
                        <div
                            data-testid="fs-reclaim-bar"
                            className="mt-7 rounded-xl p-5 md:p-6 relative overflow-hidden"
                            style={{
                                background: "linear-gradient(135deg, rgba(30,91,255,0.22) 0%, rgba(30,91,255,0.08) 100%)",
                                border: "1px solid rgba(30,91,255,0.35)",
                            }}
                        >
                            <div className="absolute inset-0 pointer-events-none"
                                 style={{
                                     background: "radial-gradient(circle at top right, rgba(30,91,255,0.28) 0%, transparent 60%)",
                                 }}
                            />
                            <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase mb-1.5"
                                         style={{ color: "rgba(147,180,255,0.9)" }}>
                                        <i className="fa-solid fa-wand-magic-sparkles mr-1.5" />
                                        Lexafide absorbs
                                    </div>
                                    <div
                                        data-testid="fs-reclaimed-hours"
                                        className="font-display text-3xl md:text-[2.4rem] leading-none font-semibold tabular-nums"
                                    >
                                        <AnimatedNum value={m.reclaimedHours} suffix=" hrs" />
                                    </div>
                                    <div className="text-[12px] text-white/65 mt-1.5">hours reclaimed / mo</div>
                                </div>
                                <div>
                                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase mb-1.5"
                                         style={{ color: "rgba(147,180,255,0.9)" }}>
                                        Equivalent staff cost
                                    </div>
                                    <div
                                        data-testid="fs-dollar-value"
                                        className="font-display text-3xl md:text-[2.4rem] leading-none font-semibold tabular-nums accent-blue"
                                        style={{ color: "#93B4FF" }}
                                    >
                                        <AnimatedNum value={m.dollarValue} prefix="$" />
                                    </div>
                                    <div className="text-[12px] text-white/65 mt-1.5">/ year in equivalent staff cost</div>
                                </div>
                            </div>
                        </div>

                        {/* Where the hours go — mini stacked bar breakdown (mind-revealing) */}
                        <div className="mt-6" data-testid="fs-hours-breakdown">
                            <div className="flex items-center justify-between mb-2.5">
                                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/50">
                                    Where the hours go
                                </span>
                                <span className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-white/40">
                                    % of ops load
                                </span>
                            </div>
                            <HoursBreakdown metrics={m} />
                        </div>

                        {/* Step 4 CTA — served firms vs solo carve-out */}
                        <div className="mt-auto pt-7">
                            {SERVED_FIRM_IDS.has(firmId) ? (
                                <div className="rounded-xl bg-white/[0.05] border border-white/12 p-5 md:p-6" data-testid="fs-report-cta">
                                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2">
                                        Step 4 · Full report
                                    </div>
                                    <h3 className="font-display text-lg md:text-xl font-semibold leading-tight text-white">
                                        Get the full operations report for a firm like yours.
                                    </h3>
                                    <p className="mt-2.5 text-[13.5px] text-white/70 leading-relaxed">
                                        A detailed breakdown of where the hours go — reception, intake, records retrieval, adjuster follow-ups, status updates, document chasing, after-hours coverage — and which Lexafide agent absorbs each one.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setModalOpen(true)}
                                        data-testid="fs-download-btn"
                                        className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[var(--ink)] font-medium text-sm hover:bg-[var(--blue)] hover:text-white transition-colors duration-300"
                                    >
                                        <i className="fa-solid fa-file-lines text-xs" />
                                        Download the full report
                                    </button>
                                </div>
                            ) : (
                                <div
                                    data-testid="fs-solo-carveout"
                                    className="rounded-xl p-5 md:p-6 relative overflow-hidden"
                                    style={{
                                        background: "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(30,91,255,0.10) 100%)",
                                        border: "1px solid rgba(255,255,255,0.14)",
                                    }}
                                >
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                                            <i className="fa-solid fa-handshake text-[13px] text-white" />
                                        </span>
                                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/60">
                                            A note for solo practitioners
                                        </div>
                                    </div>
                                    <h3 className="font-display text-lg md:text-xl font-semibold leading-tight text-white">
                                        We&apos;re not built for solo practices <span className="text-white/70">— yet.</span>
                                    </h3>
                                    <p className="mt-2.5 text-[13.5px] text-white/75 leading-relaxed">
                                        Today Lexafide is built for firms where ops start straining under headcount — usually three attorneys and up. If you&apos;re running solo, you already are the operations. We&apos;d be honored to share what we&apos;ve learned along the way — and when you&apos;re ready to scale, we&apos;re here.
                                    </p>
                                    <div className="mt-4 flex flex-wrap gap-2.5">
                                        <a
                                            href="https://calendly.com/d/d3nd-zhb-2fr/lexafide-demo-discussion-30-minute-meeting-web"
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid="fs-solo-consult-btn"
                                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-[var(--ink)] font-medium text-[13px] hover:bg-[var(--blue)] hover:text-white transition-colors duration-300"
                                        >
                                            <i className="fa-solid fa-mug-hot text-[11px]" />
                                            Free 30-min consult
                                        </a>
                                        <a
                                            href="/contact"
                                            data-testid="fs-solo-stay-btn"
                                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/25 text-white/85 font-medium text-[13px] hover:bg-white/10 transition-colors duration-300"
                                        >
                                            Keep me posted
                                            <i className="fa-solid fa-arrow-right text-[10px]" />
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Lead capture modal */}
            {modalOpen && (
                <LeadModal
                    firm={firm}
                    intakes={intakes}
                    opsHours={m.opsHours}
                    onClose={() => setModalOpen(false)}
                />
            )}

            {/* Local slider styles (same treatment as the old ROI slider) */}
            <style>{`
                .fs-slider {
                    -webkit-appearance: none;
                    appearance: none;
                    height: 4px;
                    background: var(--border-strong);
                    border-radius: 999px;
                    outline: none;
                }
                .fs-slider::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    background: var(--ink);
                    border: 3px solid var(--paper);
                    box-shadow: 0 2px 8px rgba(0,0,0,0.18);
                    cursor: pointer;
                    transition: transform 0.18s ease;
                }
                .fs-slider::-webkit-slider-thumb:hover {
                    transform: scale(1.1);
                    background: var(--blue-deep);
                }
                .fs-slider::-moz-range-thumb {
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    background: var(--ink);
                    border: 3px solid var(--paper);
                    box-shadow: 0 2px 8px rgba(0,0,0,0.18);
                    cursor: pointer;
                }
            `}</style>
        </section>
    );
};

const MiniStat = ({ label, value, testid }) => (
    <div className="rounded-lg bg-[var(--cream-2)] border border-[var(--border)] px-3.5 py-2.5">
        <div className="font-mono text-[9.5px] tracking-[0.16em] uppercase text-[var(--ink-dim)]">{label}</div>
        <div
            data-testid={testid}
            className="font-display text-lg font-semibold tabular-nums text-[var(--ink)] leading-none mt-1"
        >
            {value.toLocaleString()}
        </div>
    </div>
);

/* Small horizontal stacked bar that reveals *where* the ops hours go.
   Segments animate on width change (framer motion layout transitions). */
const HOURS_SEGMENTS = [
    { key: "receptionHours",  label: "Reception",       color: "#1E5BFF",  short: "Reception & call handling" },
    { key: "intakeHours",     label: "Intake",          color: "#7C5BFF",  short: "Intake processing & data entry" },
    { key: "followupHours",   label: "Follow-ups",      color: "#F0DDA1",  short: "Records, signatures, adjusters" },
    { key: "voicemailHours",  label: "After-hours",     color: "#DB6B4F",  short: "Voicemail & after-hours" },
];

const HoursBreakdown = ({ metrics }) => {
    const total = metrics.opsHours || 1;
    const segments = HOURS_SEGMENTS.map((s) => ({
        ...s,
        hours: metrics[s.key],
        pct: (metrics[s.key] / total) * 100,
    }));
    return (
        <>
            {/* Stacked bar */}
            <div className="h-2.5 rounded-full overflow-hidden flex bg-white/8" style={{ background: "rgba(255,255,255,0.08)" }}>
                {segments.map((s, i) => (
                    <motion.span
                        key={s.key}
                        aria-hidden
                        initial={false}
                        animate={{ width: `${s.pct}%` }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className={`h-full block ${i === 0 ? "rounded-l-full" : ""} ${i === segments.length - 1 ? "rounded-r-full" : ""}`}
                        style={{ background: s.color }}
                    />
                ))}
            </div>
            {/* Legend */}
            <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
                {segments.map((s) => (
                    <li key={s.key} className="flex items-center gap-2.5 text-[12px]">
                        <span
                            aria-hidden
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ background: s.color }}
                        />
                        <span className="text-white/75 truncate">{s.short}</span>
                        <span className="ml-auto font-mono tabular-nums text-white/90 text-[11px]">
                            {Math.round(s.hours).toLocaleString()}<span className="text-white/45"> hrs</span>
                        </span>
                    </li>
                ))}
            </ul>
        </>
    );
};

const LeadModal = ({ firm, intakes, opsHours, onClose }) => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [firmName, setFirmName] = useState("");
    const [status, setStatus] = useState("idle"); // idle | submitting | success | error
    const [errorMsg, setErrorMsg] = useState("");
    const [reportHref, setReportHref] = useState("");

    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !emailValid) return;
        setStatus("submitting");
        setErrorMsg("");
        try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: name.trim(),
                    email: email.trim(),
                    firm_name: firmName.trim() || null,
                    firm_type: firm.label,
                    firm_id: firm.id,
                    monthly_intakes: intakes,
                    ops_hours: Math.round(opsHours * 10) / 10,
                }),
            });
            if (!resp.ok) throw new Error(`Server responded ${resp.status}`);
            // Build the report URL; do NOT auto-open (window.open after an
            // await often gets blocked by modern browsers). The success
            // state shows a big "Open my report" button — that click is a
            // fresh user gesture, never blocked. A copy of the report link
            // is also emailed by the backend.
            const q = new URLSearchParams({
                firm: firm.id,
                intakes: String(intakes),
                name: name.trim(),
            });
            setReportHref(`/report?${q.toString()}`);
            setStatus("success");
        } catch (err) {
            setStatus("idle");
            setErrorMsg("Something went wrong. Please try again.");
        }
    };

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[rgba(24,24,27,0.55)] backdrop-blur-sm"
            data-testid="fs-lead-modal"
            onClick={onClose}
        >
            <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-md rounded-2xl bg-[var(--paper)] shadow-[0_40px_120px_-40px_rgba(0,0,0,0.5)] p-6 md:p-8"
            >
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    data-testid="fs-modal-close"
                    className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-[var(--ink-soft)] hover:bg-[var(--cream-2)] transition-colors"
                >
                    <i className="fa-solid fa-xmark text-sm" />
                </button>

                <span className="eyebrow-pill">Your operations report</span>
                {status === "success" ? (
                    <div data-testid="fs-lead-success">
                        <h3 className="font-display text-2xl md:text-[1.75rem] font-semibold leading-tight text-[var(--ink)] mt-4">
                            On its way to <span className="accent-blue">{email}</span>.
                        </h3>
                        <p className="mt-3 text-[14px] text-[var(--ink-soft)] leading-relaxed">
                            A copy is on its way to your inbox. Open your personalized report below.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            {reportHref && (
                                <a
                                    href={reportHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="fs-lead-open-report"
                                    className="btn-primary inline-flex items-center gap-2 text-sm"
                                >
                                    <i className="fa-solid fa-file-lines text-xs" />
                                    Open my report
                                </a>
                            )}
                            <button
                                type="button"
                                onClick={onClose}
                                data-testid="fs-lead-done"
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[var(--border-strong)] bg-[var(--paper)] text-[var(--ink)] font-medium text-sm hover:border-[var(--ink)] transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <h3 className="font-display text-2xl md:text-[1.75rem] font-semibold leading-tight text-[var(--ink)] mt-4">
                            Where should we send it?
                        </h3>
                        <p className="mt-2 text-[14px] text-[var(--ink-soft)] leading-relaxed">
                            We&apos;ll open your personalized report and email you a copy for later.
                        </p>

                        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="fs-lead-form">
                    <Field label="Full name" required>
                        <input
                            type="text"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Maria Santos"
                            data-testid="fs-lead-name"
                            className="form-input"
                        />
                    </Field>
                    <Field label="Work email" required>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="m.santos@yourfirm.com"
                            data-testid="fs-lead-email"
                            className="form-input"
                        />
                    </Field>
                    <Field label="Firm name (optional)">
                        <input
                            type="text"
                            value={firmName}
                            onChange={(e) => setFirmName(e.target.value)}
                            placeholder="Richmond Vona PI"
                            data-testid="fs-lead-firm"
                            className="form-input"
                        />
                    </Field>

                    {errorMsg && (
                        <div className="text-[13px] text-[#DC2626]" data-testid="fs-lead-error">{errorMsg}</div>
                    )}

                    <button
                        type="submit"
                        disabled={status === "submitting" || !name.trim() || !emailValid}
                        data-testid="fs-lead-submit"
                        className="btn-primary w-full inline-flex items-center justify-center gap-2 text-sm disabled:opacity-60"
                    >
                        {status === "submitting" ? "Sending…" : "Get my report"}
                        <i className="fa-solid fa-arrow-right text-[10px]" />
                    </button>

                    <p className="text-[11.5px] text-[var(--ink-dim)] leading-relaxed">
                        We&apos;ll send you the report and occasional operational insights for plaintiff firms. Unsubscribe anytime.
                    </p>
                </form>
                    </>
                )}
            </motion.div>
        </div>
    );
};

const Field = ({ label, required, children }) => (
    <label className="block">
        <span className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink-dim)] font-semibold mb-1.5">
            {label}{required && <span className="text-[#DC2626] ml-1">*</span>}
        </span>
        {children}
    </label>
);

export default FirmOperationsSnapshot;
export { FIRM_TYPES, computeMetrics };
